import { Navigate, Outlet, useLocation } from "react-router-dom";

import type { Location } from "react-router-dom";

import { useAuth, type Role } from "@/contexts/auth-context";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import { ADMIN_ROLES, CLINICIAN_ROLES } from "@/routes/route-access";

// gh-461 — INVARIANT (intentionally satisfied, no guard change): every guard keys off
// `status`/`user`/`hasRole`/`isLegacyGated` and NEVER reads `accessToken`. So an offline-restored
// session (`status:"authenticated"`, `accessToken:null`) passes exactly like a full session. Locked
// by a regression test in guards.test.tsx.

// Full target path (pathname + query + hash) so post-login return-to-route can
// restore deep links like /clients?tab=active#row-3, not just the bare path.
function returnTo(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

// Neutral, token-only loading surface shown while the boot silent-refresh resolves.
export function FullPageLoading() {
  return (
    <main
      aria-busy="true"
      className="flex min-h-screen items-center justify-center text-sm text-[color:var(--text-tertiary)]"
    >
      Loading…
    </main>
  );
}

/**
 * Requires an authenticated session only (no shell, no force-change redirect).
 * Used for standalone authenticated routes such as /forced-password-change so the
 * forced-change redirect cannot loop back onto itself.
 */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullPageLoading />;
  if (status === "unauthenticated") {
    return <Navigate to="/sign-in" replace state={{ from: returnTo(location) }} />;
  }
  return <Outlet />;
}

/**
 * Authenticated + force-change gate for in-shell routes. Redirects:
 *  - unauthenticated → /sign-in
 *  - force_password_change set → /forced-password-change
 * On success returns <Outlet />; the AppShell chrome is rendered by the nested
 * route in app-routes.tsx, not here.
 */
export function ProtectedLayout() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullPageLoading />;
  if (status === "unauthenticated") {
    return <Navigate to="/sign-in" replace state={{ from: returnTo(location) }} />;
  }
  if (user?.forcePasswordChange) {
    return <Navigate to="/forced-password-change" replace />;
  }
  return <Outlet />;
}

/**
 * Story 16.2 — account-STATE gate (orthogonal to RoleGuard's role gate). A
 * legacy clinician still in stage-1 lockdown (isLegacyGated) holds role
 * `clinician` and passes the role gate, but must NOT reach the locked
 * surfaces (Workspace / Clients / Evaluations / Results) — wrap those route
 * groups in this guard so a gated user who deep-links or types a locked URL is
 * sent to /profile. The backend `get_current_user` gate (403 LEGACY-001) is the
 * real enforcement authority; this only spares the gated user a dead-end fetch
 * and mirrors the locked-nav UX. Non-gated users fall straight through.
 */
export function RequireUnlocked() {
  const { isLegacyGated } = useAuth();
  if (isLegacyGated) {
    return <Navigate to="/profile" replace />;
  }
  return <Outlet />;
}

/**
 * Story 8.1 (FR38) — first-login calibration routing. A clinician whose
 * calibration is incomplete is bounced off the workspace group to
 * /calibration/overview until all 5 case studies are done (E8) or the
 * already_certified bypass applies.
 *
 * Evaluated LAST among the account gates (M4 ordering: forced password change
 * 16.1 → legacy lock 16.2 → PWA install gate E7 → calibration) — mount it
 * inside RequireUnlocked/SurfaceGuard, never outside them.
 *
 * Decision ladder, cheapest first:
 *  1. non-clinician → pass (admins have no calibration).
 *  2. already_certified JWT claim → synchronous pass — zero status queries,
 *     offline-safe (post-backfill this is every pre-8.1 account).
 *  3. GET /calibration/status → state !== "complete" ⇒ redirect.
 *  4. Query error (offline, 5xx) → FAIL OPEN: FE gating mirrors UX; the
 *     backend require_calibration_ready (403 CALIB-001) is the enforcement
 *     authority, and the offline drain treats that code as non-retryable.
 */
export function CalibrationGate() {
  const { user, hasRole } = useAuth();
  const isClinician = hasRole(...CLINICIAN_ROLES);
  const bypass = user?.alreadyCertified === true;
  const needsStatus = isClinician && !bypass;
  const { data, isPending, isError } = useCalibrationStatus({ enabled: needsStatus });

  if (!needsStatus) return <Outlet />;
  if (isPending) return <FullPageLoading />;
  // `data?.state` (not `data.state`): after the isPending early-return TanStack v5
  // still types `data` as `CalibrationStatusData | undefined` in the destructured
  // form — control-flow narrowing does not carry `!isPending` + `!isError` across
  // the two separate conditions. Runtime is safe (success ⇒ data defined), but the
  // optional chain keeps it defensive and fail-SAFE: a would-be-undefined `state`
  // reads `undefined !== "complete"` ⇒ routes INTO calibration, never silently past.
  if (!isError && data?.state !== "complete") {
    return <Navigate to="/calibration/overview" replace />;
  }
  return <Outlet />;
}

interface RoleGuardProps {
  allow: ReadonlyArray<Role | string>;
}

/**
 * Claims-driven route-level role gate. Unauthorized (authenticated but wrong role)
 * users go to the /403 surface — NOT /sign-in (that is for unauthenticated). The
 * backend remains the enforcement authority; this only mirrors which roles may
 * reach a route (Story 2.8 matrix intent).
 */
export function RoleGuard({ allow }: RoleGuardProps) {
  const { hasRole } = useAuth();
  if (!hasRole(...allow)) {
    return <Navigate to="/403" replace />;
  }
  return <Outlet />;
}

// Picks the right landing route per role (admins have no /workspace nav entry).
// Keyed off ADMIN_ROLES (route-access.ts, the single source of truth) rather than
// hardcoded role strings: a future admin role added there must NOT fall through to
// /workspace, which is now a CLINICIAN_ROLES-gated route (→ /403 loop on login).
export function HomeRedirect() {
  const { user, isLegacyGated } = useAuth();
  // Story 16.2 — a gated legacy clinician's home is Profile, not /workspace
  // (which is locked for them). Checked first so it wins over the role split.
  if (isLegacyGated) {
    return <Navigate to="/profile" replace />;
  }
  // Story 14.1 — an org_admin lands on their org dashboard (FR76 "land on my
  // org dashboard"). Checked before the ADMIN_ROLES split so it wins for
  // org_admin — which is what makes the super_admin-only landing below safe
  // despite ADMIN_ROLES containing both roles.
  if (user?.role === "org_admin") {
    return <Navigate to="/org-admin/dashboard" replace />;
  }
  // `role` is an untrusted token claim (Role | string), so widen the typed
  // allow-list to compare — same pattern as canRoleAccessPath in route-access.ts.
  const isAdmin = user?.role !== undefined && (ADMIN_ROLES as readonly string[]).includes(user.role);
  // Story 15.7 (D7) — the operational console is the super_admin's Dashboard and
  // the first nav entry, so it is where a sign-in lands. It replaced /admin/users,
  // which was the landing only because no console existed to land on.
  //
  // A future admin ROLE added to ADMIN_ROLES would land here too, and the console
  // is `/admin/console`-gated to SUPER_ADMIN_ROLES — so such a role must either
  // be added to that rule or given its own branch above, exactly as org_admin has.
  const target = isAdmin ? "/admin/console" : "/workspace";
  return <Navigate to={target} replace />;
}
